import { useState, useEffect, useRef } from "react";
import Card from "components/card";
import { instructorAuthService } from "services/instructorAuthService";
import {
  instructorNotificationPreferencesService,
  type NotificationPreferences,
} from "services/instructorNotificationPreferencesService";
import { r2Service } from "services/r2Service";
import { AiOutlineCamera, AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useAppLanguage } from "hooks/useAppLanguage";
import { normalizeIsActive } from "lib/normalizeIsActive";
import { DEFAULT_USER_AVATAR } from "components/ui/UserAvatar";

interface Profile {
  id: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  image?: string | null;
  image_url?: string | null;
  bio?: string | null;
  is_active?: boolean | number | string;
  status_label?: "active" | "inactive" | string;
  roles?: string[];
  permissions?: string[];
}

const DEFAULT_PREFS: NotificationPreferences = {
  chat_enabled: true,
  announcements_enabled: true,
  system_enabled: true,
};

export default function InstructorProfilePage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchProfile = async () => {
    try {
      const [data, notificationPrefs] = await Promise.all([
        instructorAuthService.getProfile(),
        instructorNotificationPreferencesService.get().catch(() => DEFAULT_PREFS),
      ]);
      setProfile(data as any);
      setName((data as any)?.name || "");
      setEmail((data as any)?.email || "");
      setBio((data as any)?.bio || "");
      setPrefs({ ...DEFAULT_PREFS, ...notificationPrefs });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const getImageUrl = (imagePath: string | undefined | null): string => {
    if (!imagePath || imagePath === "null" || imagePath === "undefined") {
      return DEFAULT_USER_AVATAR;
    }
    if (imagePath.startsWith("http") || imagePath.startsWith("data:image/") || imagePath.startsWith("blob:")) {
      return imagePath;
    }
    return DEFAULT_USER_AVATAR;
  };

  const buildProfilePayload = (nextImage = profile?.image) => ({
    name: name.trim() || profile?.name || profile?.phone || "Instructor",
    email: email.trim() || null,
    image: nextImage || "",
    bio: bio.trim() || null,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setMessage(null);

    try {
      const result = await r2Service.uploadWithPresign(file, "avatars");
      const updated = await instructorAuthService.updateProfile(buildProfilePayload(result.key));
      setProfile(updated as any);
      setMessage({ type: "success", text: isArabic ? "تم تحديث صورة الملف الشخصي." : "Profile image updated!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error("Image upload failed:", error);
      setMessage({
        type: "error",
        text: error?.message || (isArabic ? "فشل رفع الصورة" : "Failed to upload image"),
      });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: "error", text: isArabic ? "الاسم مطلوب" : "Name is required" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const response = await instructorAuthService.updateProfile(buildProfilePayload());
      setProfile(response as any);
      setMessage({ type: "success", text: isArabic ? "تم تحديث الملف الشخصي بنجاح." : "Profile updated successfully!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      setMessage({
        type: "error",
        text: error?.response?.data?.message || (isArabic ? "فشل تحديث الملف الشخصي" : "Failed to update profile"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!currentPassword.trim()) {
      setPasswordMessage({
        type: "error",
        text: isArabic ? "كلمة المرور الحالية مطلوبة" : "Current password is required",
      });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({
        type: "error",
        text: isArabic ? "كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل" : "New password must be at least 6 characters",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        type: "error",
        text: isArabic ? "كلمتا المرور غير متطابقتين" : "Passwords do not match",
      });
      return;
    }

    setPasswordSaving(true);
    setPasswordMessage(null);

    try {
      await instructorAuthService.updatePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage({
        type: "success",
        text: isArabic ? "تم تحديث كلمة المرور بنجاح." : "Password updated successfully!",
      });
      setTimeout(() => setPasswordMessage(null), 3000);
    } catch (error: any) {
      const validationErrors = error?.response?.data?.errors;
      const firstValidationError =
        validationErrors?.current_password?.[0] ||
        validationErrors?.password?.[0] ||
        validationErrors?.password_confirmation?.[0];
      setPasswordMessage({
        type: "error",
        text:
          firstValidationError ||
          error?.response?.data?.message ||
          (isArabic ? "فشل تحديث كلمة المرور" : "Failed to update password"),
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const passwordInputClassName =
    "h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white";

  const passwordToggleButton = (visible: boolean, onToggle: () => void) => (
    <button
      type="button"
      onClick={onToggle}
      className="absolute top-1/2 -translate-y-1/2 text-gray-500 transition hover:text-brand-500 dark:text-white/60"
      style={isArabic ? { left: "1rem" } : { right: "1rem" }}
      aria-label={visible ? (isArabic ? "إخفاء كلمة المرور" : "Hide password") : isArabic ? "إظهار كلمة المرور" : "Show password"}
    >
      {visible ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
    </button>
  );

  const togglePref = async (key: keyof NotificationPreferences, value: boolean) => {
    if (prefsSaving) return;
    const previous = prefs;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setPrefsSaving(true);
    try {
      const saved = await instructorNotificationPreferencesService.update({ [key]: value });
      setPrefs({ ...DEFAULT_PREFS, ...saved });
      setMessage({
        type: "success",
        text: isArabic ? "تم تحديث إعدادات الإشعارات." : "Notification settings updated.",
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setPrefs(previous);
      setMessage({
        type: "error",
        text:
          error?.response?.data?.message ||
          (isArabic ? "فشل تحديث إعدادات الإشعارات" : "Failed to update notification settings"),
      });
    } finally {
      setPrefsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const isActive = normalizeIsActive(profile?.is_active);
  const statusLabel =
    isActive === null
      ? isArabic
        ? "غير محدد"
        : "Not specified"
      : isActive
        ? isArabic
          ? "نشط"
          : "Active"
        : isArabic
          ? "غير نشط"
          : "Inactive";
  const statusClassName =
    isActive === null ? "text-gray-500" : isActive ? "text-green-500" : "text-red-500";

  const prefRows: Array<{
    key: keyof NotificationPreferences;
    label: string;
    hint: string;
  }> = [
    {
      key: "chat_enabled",
      label: isArabic ? "إشعارات الشات" : "Chat notifications",
      hint: isArabic ? "رسائل الطلاب في كورساتك" : "Student messages in your courses",
    },
    {
      key: "system_enabled",
      label: isArabic ? "إشعارات النظام" : "System notifications",
      hint: isArabic ? "تنبيهات الإدارة والتنبيهات العامة" : "Admin broadcasts and general alerts",
    },
  ];

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "إعدادات الملف الشخصي" : "Profile Settings"}
          </h1>
          <p className="text-gray-500">
            {isArabic ? "إدارة إعدادات حسابك" : "Manage your account settings"}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <Card extra="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div
                  className="h-32 w-32 overflow-hidden rounded-full border-4 border-white shadow-lg"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <div className="flex h-full w-full items-center justify-center bg-gray-900/50">
                      <div className="h-8 w-8 animate-spin rounded-full border-3 border-white border-t-transparent" />
                    </div>
                  ) : (
                    <img
                      src={getImageUrl(profile?.image_url || profile?.image)}
                      alt={isArabic ? "الصورة الشخصية" : "Profile"}
                      className="h-full w-full cursor-pointer object-cover"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.src !== DEFAULT_USER_AVATAR) {
                          img.src = DEFAULT_USER_AVATAR;
                        }
                      }}
                    />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg transition hover:bg-brand-600"
                >
                  <AiOutlineCamera size={18} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>

              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                {profile?.name || profile?.phone || (isArabic ? "مدرّس" : "Instructor")}
              </h3>
              <p className="text-sm text-gray-500">{profile?.phone || "—"}</p>

              <div className="mt-6 w-full space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-navy-700">
                  <span className="text-sm text-gray-500">{isArabic ? "الحالة" : "Status"}</span>
                  <span className={`text-sm font-medium ${statusClassName}`}>{statusLabel}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-navy-700">
                  <span className="text-sm text-gray-500">ID</span>
                  <span className="text-sm font-medium text-navy-700 dark:text-white">#{profile?.id}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <Card extra="p-6">
            <h3 className="mb-6 text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "البيانات الشخصية" : "Personal Information"}
            </h3>

            {message && (
              <div
                className={`mb-4 rounded-lg px-4 py-3 text-sm ${
                  message.type === "success"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {message.text}
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "الاسم الكامل *" : "Full Name *"}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isArabic ? "أدخل اسمك" : "Enter your name"}
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "البريد الإلكتروني" : "Email Address"}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isArabic ? "أدخل بريدك الإلكتروني" : "Enter your email address"}
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "رقم الهاتف" : "Phone Number"}
                </label>
                <input
                  type="tel"
                  value={profile?.phone || "—"}
                  disabled
                  className="h-12 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-500 dark:border-white/10 dark:bg-navy-800 dark:text-white/50"
                />
                <p className="mt-2 text-xs text-gray-500">
                  {isArabic
                    ? "رقم الهاتف مرتبط بحسابك ولا يمكن تعديله من هنا."
                    : "Your phone number is tied to your account and cannot be changed here."}
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "الوصف" : "Bio"}
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder={isArabic ? "اكتب نبذة عنك تظهر للطلاب" : "Write a short bio about yourself"}
                  rows={4}
                  className="w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className="linear flex items-center justify-center rounded-xl bg-brand-500 px-8 py-3 text-base font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : isArabic ? (
                    "حفظ التعديلات"
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </Card>

          <Card extra="p-6">
            <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "كلمة المرور" : "Password"}
            </h3>
            <p className="mb-6 text-sm text-gray-500">
              {isArabic
                ? "حدّث كلمة المرور المستخدمة لتسجيل الدخول"
                : "Update the password used to sign in"}
            </p>

            {passwordMessage && (
              <div
                className={`mb-4 rounded-lg px-4 py-3 text-sm ${
                  passwordMessage.type === "success"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {passwordMessage.text}
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "كلمة المرور الحالية" : "Current Password"}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className={`${passwordInputClassName} ${isArabic ? "pl-12" : "pr-12"}`}
                  />
                  {passwordToggleButton(showCurrentPassword, () => setShowCurrentPassword((v) => !v))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "كلمة المرور الجديدة" : "New Password"}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={isArabic ? "6 أحرف على الأقل" : "At least 6 characters"}
                    autoComplete="new-password"
                    className={`${passwordInputClassName} ${isArabic ? "pl-12" : "pr-12"}`}
                  />
                  {passwordToggleButton(showNewPassword, () => setShowNewPassword((v) => !v))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "تأكيد كلمة المرور الجديدة" : "Confirm New Password"}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className={`${passwordInputClassName} ${isArabic ? "pl-12" : "pr-12"}`}
                  />
                  {passwordToggleButton(showConfirmPassword, () => setShowConfirmPassword((v) => !v))}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handlePasswordSave}
                  disabled={
                    passwordSaving ||
                    !currentPassword.trim() ||
                    !newPassword.trim() ||
                    !confirmPassword.trim()
                  }
                  className="linear flex items-center justify-center rounded-xl border border-brand-500 px-8 py-3 text-base font-medium text-brand-500 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/5"
                >
                  {passwordSaving ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                  ) : isArabic ? (
                    "تحديث كلمة المرور"
                  ) : (
                    "Update Password"
                  )}
                </button>
              </div>
            </div>
          </Card>

          <Card extra="p-6">
            <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "الإشعارات" : "Notifications"}
            </h3>
            <p className="mb-6 text-sm text-gray-500">
              {isArabic
                ? "تحكم في إشعارات الدفع التي تصلك على الجهاز"
                : "Control which push alerts reach this device"}
            </p>

            <div className="space-y-3">
              {prefRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/10 dark:bg-navy-700"
                >
                  <div className={isArabic ? "text-right" : "text-left"}>
                    <p className="text-sm font-medium text-navy-700 dark:text-white">{row.label}</p>
                    <p className="text-xs text-gray-500">{row.hint}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prefs[row.key]}
                    disabled={prefsSaving}
                    onClick={() => void togglePref(row.key, !prefs[row.key])}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                      prefs[row.key] ? "bg-brand-500" : "bg-gray-300 dark:bg-navy-800"
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${
                        prefs[row.key] ? "right-0.5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
